import { type QueryClient, useQuery } from '@tanstack/react-query'
import {
  type RichTextDocumentInclude,
  OpalApiResponseDocument,
  richTextDocuments,
} from '@opallabs/api/v3'
import { queryKeys } from '../queryKeys'

export type RichTextDocument = OpalApiResponseDocument<
  'ReadRichTextDocumentV3',
  200
>

const DOCUMENT_INCLUDE: RichTextDocumentInclude = [
  'assets',
  'embedded_resources',
]

const richTextQuery = (documentId: string) => ({
  queryKey: queryKeys.document(documentId),
  queryFn: async () => {
    return richTextDocuments.readSingle({
      path: {
        rich_text_document_id: documentId,
      },
      query: {
        include: DOCUMENT_INCLUDE,
      },
    })
  },
})

/**
 * Starts fetching a document before its editor mounts, e.g. from a route
 * loader. `<CollaborationTextEditor>` shows its body until the document syncs.
 */
export const prefetchRichText = (
  queryClient: QueryClient,
  documentId: string
) => queryClient.prefetchQuery(richTextQuery(documentId))

export const useRichText = <T>({
  documentId,
  select,
  enabled = true,
  refetchInterval,
}: {
  documentId: string
  select?: (document?: RichTextDocument) => T
  enabled?: boolean
  refetchInterval?: number | false | ((data: T | undefined) => number | false)
}) => {
  return useQuery({
    ...richTextQuery(documentId),
    select,
    enabled,
    refetchInterval,
  })
}
